/**
 * /tenants/$slug/compliance — redirect to the Posture address.
 *
 * Compliance moved under Posture because that is where the sidebar has
 * always put it and the URL said otherwise. The old address stays because
 * it is in bookmarks, in runbooks, and in links people have already sent
 * to their auditors — and a compliance page that 404s for an auditor is
 * the worst possible moment for a broken link.
 *
 * `replace` so the redirect does not sit in history and trap the back
 * button. Search params carry through: a saved "not met, critical only"
 * link has to land on those rows, not on an unfiltered page.
 */

import { createFileRoute, redirect } from "@tanstack/react-router";

/** The tabs the target route accepts. */
type Tab = "reports" | "frameworks" | "evidence" | "exports";

/** Where each retired tab's content went. */
function remapTab(old: unknown): Tab | undefined {
  switch (old) {
    case "posture":
    case "exceptions":
      // The chooser, the framework grid and the standing treatments.
      return "frameworks";
    case "evidence":
      return "evidence";
    case "exports":
      return "exports";
    case "reports":
      return "reports";
    case "frameworks":
      return "frameworks";
    default:
      return undefined;
  }
}

export const Route = createFileRoute("/_authed/tenants/$slug/compliance/")({
  // validateSearch is an identity pass-through, and it is load-bearing:
  // without it the router drops every param this route does not declare,
  // so a saved "not met, critical only" link would land unfiltered. The
  // target route validates them properly.
  validateSearch: (raw: Record<string, unknown>) => raw,
  beforeLoad: ({ params, search }) => {
    throw redirect({
      to: "/tenants/$slug/posture/compliance",
      params,
      // The TAB NAMES CHANGED with the move, and forwarding the old ones
      // verbatim was worse than dropping them: `.catch(undefined)` on the
      // target swallowed `posture` and `exceptions` and landed the reader
      // on Reports with no sign anything was lost. Both of those views
      // now live under Frameworks, so a saved link goes where its content
      // went. That is the whole point of keeping the old address.
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        ...search,
        tab: remapTab(search["tab"]),
      }),
      replace: true,
    });
  },
});
